import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
    Mic,
    Headphones,
    ArrowRight,
    BookOpen,
    Settings,
    Shield,
    Terminal,
    FlaskConical,
    MessageSquareText,
    UserSearch,
    BookMarked,
    ClipboardCheck,
    ClipboardList
} from 'lucide-react';
import Card from './ui/Card';
import Button from './ui/Button';
import { canAccessManualQaAdmin, canAccessManualQaRunner } from '../manualQaRoutePolicy';
import { canReadPlatformAdmin } from '../utils/platformAccess';
import './RoleSelection-revised.css';

/**
 * Build the role-gated navigation sections for the landing page.
 *
 * Single source of truth for every non-session route reachable from the home
 * screen. Each section (and each item) declares its own visibility so new tools
 * get a labelled home by appending here — never by wedging another button into
 * an unrelated card. Item visibility falls back to the section's when omitted.
 *
 * Nav visibility is intentionally *at least as restrictive as* the App-revised.js
 * route guards — never looser. In two cases it is deliberately tighter than the
 * guard (so a card is hidden even though the route is technically reachable by
 * URL); those are called out below. It is not a 1:1 mirror of the guards.
 * - Administration  → settings and user lookup use canReadPlatformAdmin, matching
 *                     their routes; Prompt Settings keeps the Admin route policy.
 * - Glossary        → requiredRole="Host" (+ platform access); same as guard.
 * - Quality Assurance → admin-capable users + c2hl-provisioned Manual QA testers
 *                       (canAccessManualQaRunner). TIGHTER than the guard: the
 *                       `/manual-qa` runner route sets no requiredRole (any
 *                       authenticated user can load it by URL), but the card is
 *                       hidden from plain Host/Participant. QA Admin card is gated
 *                       to canAccessManualQaAdmin.
 * - Developer Tools → platform staff only (SuperAdmin / IsPlatformAdmin). TIGHTER
 *                     than the guard: ProtectedRoute's hasPlatformAccess also
 *                     admits tenant Admin to the requiredRole="SuperAdmin" routes,
 *                     but the cards stay hidden from tenant Admin by design.
 */
function buildNavSections(user) {
    // Read both casings: the stored user profile is PascalCase on some paths
    // (dev bypass, OAuth payloads) and camelCase on others (jwtService decodes
    // `role`/`tenantId`, merged in on token refresh — AuthContext). The sibling
    // manualQaRoutePolicy.js normalizes the same way; mirror it here so a
    // camelCase-shaped user doesn't silently lose every tool section.
    const role = user?.Role || user?.role || '';
    const isPlatformStaff = Boolean(user?.IsPlatformAdmin || user?.isPlatformAdmin) || role === 'SuperAdmin';
    const isAdmin = isPlatformStaff || role === 'Admin';
    const canReadSettings = canReadPlatformAdmin(user);
    const isHostCapable = isAdmin || role === 'Host';
    const canQaAdmin = canAccessManualQaAdmin(user);
    const canQaRunner = canAccessManualQaRunner(user);
    const showQa = isAdmin || canQaRunner;

    return [
        {
            id: 'administration',
            label: 'Administration',
            visible: isAdmin || canReadSettings,
            items: [
                { id: 'admin-settings', label: 'Admin Settings', description: 'Platform & tenant configuration', icon: Shield, path: '/admin-settings', visible: canReadSettings },
                { id: 'prompt-settings', label: 'Prompt Settings', description: 'Translation prompt tuning', icon: MessageSquareText, path: '/prompt-settings', visible: isAdmin },
                { id: 'find-user', label: 'Find User', description: 'Look up a user by QolSync handle', icon: UserSearch, path: '/find-user', visible: canReadSettings }
            ]
        },
        {
            id: 'glossary',
            label: 'Glossary',
            visible: isHostCapable,
            items: [
                { id: 'glossary', label: 'Glossary Management', description: 'Custom term pairs & domain library', icon: BookMarked, path: '/glossary' }
            ]
        },
        {
            id: 'quality-assurance',
            label: 'Quality Assurance',
            visible: showQa,
            items: [
                { id: 'manual-qa', label: 'Manual QA Runner', description: 'Execute manual QA checklists', icon: ClipboardCheck, path: '/manual-qa', visible: showQa },
                { id: 'manual-qa-admin', label: 'QA Admin', description: 'Manage QA campaigns & triage', icon: ClipboardList, path: '/manual-qa/admin', visible: canQaAdmin }
            ]
        },
        {
            id: 'developer-tools',
            label: 'Developer Tools',
            visible: isPlatformStaff,
            items: [
                { id: 'api-harness', label: 'API Harness', description: 'Ad-hoc API endpoint testing', icon: Terminal, path: '/api-harness' },
                { id: 'scenario-testing', label: 'Scenario Testing', description: 'Scenario-based test harness', icon: FlaskConical, path: '/scenario-testing' },
                { id: 'qa-session-lab', label: 'QA Session Lab', description: 'Provision a disposable Q&A session', icon: FlaskConical, path: '/qa-session-lab' }
            ]
        }
    ]
        .filter((section) => section.visible)
        .map((section) => ({
            ...section,
            items: section.items.filter((item) => item.visible !== false)
        }))
        .filter((section) => section.items.length > 0);
}

const RoleSelectionRevised = () => {
    const navigate = useNavigate();
    const { user } = useAuth();

    const handleRoleSelect = (role) => {
        if (role === 'host') {
            navigate('/host');
        } else {
            navigate('/receiver');
        }
    };

    const navSections = buildNavSections(user);

    return (
        <div className="role-page">
            <div className="role-bg-elements">
                <div className="orb orb-1"></div>
                <div className="orb orb-2"></div>
            </div>

            <div className="container role-container">


                <header className="role-header animate-fade-in">
                    <div className="role-brand">
                        <div className="brand-icon">
                            <img src="/assets/qolsync-logo-animated.svg" alt="QolSync AI Logo" className="brand-logo-img" />
                        </div>
                        <span className="qolsync-brand-text"><span className="brand-qol">Qol</span><span className="brand-sync">Sync</span><span className="brand-ai">AI</span></span>
                    </div>
                    <h1 className="role-title">
                        Real-time translation <br />
                        <span className="text-gradient">for everyone.</span>
                    </h1>
                    <p className="role-subtitle">
                        Connect across languages instantly. Choose your role to begin the session.
                    </p>
                </header>

                <div className="role-grid">
                    {/* Host Card */}
                    <Card
                        glass
                        hover
                        className="role-card host-card animate-fade-in"
                        style={{ animationDelay: '100ms' }}
                        onClick={() => handleRoleSelect('host')}
                    >
                        <div className="role-card-content">
                            <div className="role-icon-wrapper host-icon-bg">
                                <Mic size={32} className="host-icon-color" />
                            </div>

                            <div className="role-info">
                                <h2>Host a Session</h2>
                                <p>Broadcast your speech to multiple languages in real-time.</p>

                                <ul className="feature-list">
                                    <li>Create new sessions</li>
                                    <li>Control languages & settings</li>
                                    <li>View live transcript</li>
                                </ul>
                            </div>

                            <div className="role-actions">
                                <Button
                                    variant="primary"
                                    className="w-full"
                                    icon={<ArrowRight size={18} />}
                                    iconPosition="right"
                                >
                                    Start Hosting
                                </Button>

                                <div className="secondary-actions">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigate('/host-guide');
                                        }}
                                        icon={<BookOpen size={14} />}
                                    >
                                        Guide
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigate('/host-control');
                                        }}
                                        icon={<Settings size={14} />}
                                    >
                                        Controls
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </Card>

                    {/* Receiver Card */}
                    <Card
                        glass
                        hover
                        className="role-card receiver-card animate-fade-in"
                        style={{ animationDelay: '200ms' }}
                        onClick={() => handleRoleSelect('receiver')}
                    >
                        <div className="role-card-content">
                            <div className="role-icon-wrapper receiver-icon-bg">
                                <Headphones size={32} className="receiver-icon-color" />
                            </div>

                            <div className="role-info">
                                <h2>Join as Participant</h2>
                                <p>Listen or read translations in your preferred language.</p>

                                <ul className="feature-list">
                                    <li>Join existing sessions</li>
                                    <li>Text-to-Speech support</li>
                                    <li>Personalized view</li>
                                </ul>
                            </div>

                            <div className="role-actions">
                                <Button
                                    variant="secondary"
                                    className="w-full"
                                    icon={<ArrowRight size={18} />}
                                    iconPosition="right"
                                >
                                    Join Session
                                </Button>

                                <div className="secondary-actions">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigate('/receiver-guide');
                                        }}
                                        icon={<BookOpen size={14} />}
                                    >
                                        Guide
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>

                {/* Workspaces & Tools — every non-session route gets a labelled,
                    role-gated home here. Sections and cards are driven by
                    buildNavSections() so nothing lands in an unrelated card. */}
                {navSections.length > 0 && (
                    <div className="tools-area animate-fade-in" style={{ animationDelay: '300ms' }}>
                        {navSections.map((section) => (
                            <section className="tool-section" key={section.id} aria-label={section.label}>
                                <div className="tool-section-divider">
                                    <span>{section.label}</span>
                                </div>
                                <div className="tool-grid">
                                    {section.items.map((item) => {
                                        const ItemIcon = item.icon;
                                        const go = () => navigate(item.path);
                                        return (
                                            <Card
                                                glass
                                                hover
                                                noPadding
                                                className="tool-card"
                                                key={item.id}
                                                role="button"
                                                tabIndex={0}
                                                onClick={go}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        go();
                                                    }
                                                }}
                                            >
                                                <div className="tool-card-content">
                                                    <div className="tool-card-icon">
                                                        <ItemIcon size={20} />
                                                    </div>
                                                    <div className="tool-card-text">
                                                        <span className="tool-card-title">{item.label}</span>
                                                        <span className="tool-card-desc">{item.description}</span>
                                                    </div>
                                                </div>
                                            </Card>
                                        );
                                    })}
                                </div>
                            </section>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default RoleSelectionRevised;
